import { useState, useMemo } from "react";
import { Check, Search, Scissors, Palette, Sparkles, Wind, User as UserIcon, Package } from "lucide-react";
import { Input } from "../ui/input";

/**
 * Selettore servizi raggruppati per CATEGORIA (tipo).
 * Ottimizzato sia per Web App (touch / iPad) che per Desktop.
 *
 * Features:
 * - Tab orizzontali per categoria con badge "selezionati"
 * - Campo di ricerca rapida (filtra per nome)
 * - Grid responsive 1col mobile / 2col desktop
 * - Tap target grande (44px+)
 * - Pulsante "Tutti" per visualizzare l'intero catalogo
 */

const CATEGORIE = [
  { key: "taglio",     label: "Taglio",       icon: Scissors,  color: "blue" },
  { key: "colore",     label: "Colore",       icon: Palette,   color: "pink" },
  { key: "trattamento",label: "Trattamento",  icon: Sparkles,  color: "amber" },
  { key: "piega",      label: "Piega",        icon: Wind,      color: "sky" },
  { key: "barba",      label: "Barba",        icon: UserIcon,  color: "slate" },
  { key: "altro",      label: "Altro",        icon: Package,   color: "violet" }
];

const COLOR_MAP = {
  blue:   { active: "bg-blue-500 text-white border-blue-600",     idle: "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100" },
  pink:   { active: "bg-pink-500 text-white border-pink-600",     idle: "bg-pink-50 text-pink-700 border-pink-200 hover:bg-pink-100" },
  amber:  { active: "bg-amber-500 text-white border-amber-600",   idle: "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100" },
  sky:    { active: "bg-sky-500 text-white border-sky-600",       idle: "bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-100" },
  slate:  { active: "bg-slate-700 text-white border-slate-800",   idle: "bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200" },
  violet: { active: "bg-violet-500 text-white border-violet-600", idle: "bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100" },
};

export default function ServiziSelector({ servizi = [], selectedServizi = [], onToggle }) {
  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");

  // Raggruppa servizi per tipo
  const grouped = useMemo(() => {
    const out = { taglio: [], colore: [], trattamento: [], piega: [], barba: [], altro: [] };
    for (const s of servizi) {
      const tipo = (s.tipo || "").toLowerCase();
      if (out[tipo]) out[tipo].push(s);
      else out.altro.push(s);
    }
    return out;
  }, [servizi]);

  // Conteggio selezionati per categoria
  const selectedCounts = useMemo(() => {
    const counts = { taglio: 0, colore: 0, trattamento: 0, piega: 0, barba: 0, altro: 0 };
    const nomiSelezionati = new Set(selectedServizi.map(s => s.nome));
    for (const s of servizi) {
      if (!nomiSelezionati.has(s.nome)) continue;
      const tipo = (s.tipo || "").toLowerCase();
      if (counts[tipo] !== undefined) counts[tipo] += 1;
      else counts.altro += 1;
    }
    return counts;
  }, [servizi, selectedServizi]);

  // Filtro per ricerca + tab
  const filtered = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    const matchSearch = (s) => !q || (s.nome || "").toLowerCase().includes(q);
    if (activeTab === "all") {
      return CATEGORIE.map(cat => ({
        ...cat,
        items: (grouped[cat.key] || []).filter(matchSearch)
      })).filter(c => c.items.length > 0);
    }
    return [{
      ...CATEGORIE.find(c => c.key === activeTab),
      items: (grouped[activeTab] || []).filter(matchSearch)
    }];
  }, [grouped, activeTab, search]);

  const totalSelected = selectedServizi.length;

  return (
    <div className="space-y-2" data-testid="servizi-selector">
      {/* Barra ricerca + reset */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Cerca servizio..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 h-11 text-sm"
          data-testid="input-cerca-servizio"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 text-base px-2"
            title="Pulisci ricerca"
          >
            ✕
          </button>
        )}
      </div>

      {/* Tab categorie - wrappabili su mobile, no scroll orizzontale */}
      <div className="flex gap-2 flex-wrap pb-1" data-testid="tabs-categorie">
        <button
          type="button"
          onClick={() => setActiveTab("all")}
          className={`shrink-0 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-semibold border-2 transition min-h-[44px] ${
            activeTab === "all" ? "bg-slate-800 text-white border-slate-900 shadow-md" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"
          }`}
          data-testid="tab-cat-all"
        >
          Tutti
          {totalSelected > 0 && (
            <span className="bg-white text-slate-800 rounded-full px-1.5 text-[10px] font-bold">{totalSelected}</span>
          )}
        </button>
        {CATEGORIE.map(cat => {
          const count = (grouped[cat.key] || []).length;
          if (count === 0) return null;
          const Icon = cat.icon;
          const isActive = activeTab === cat.key;
          const cls = COLOR_MAP[cat.color] || COLOR_MAP.violet;
          const sel = selectedCounts[cat.key];
          return (
            <button
              key={cat.key}
              type="button"
              onClick={() => setActiveTab(cat.key)}
              className={`shrink-0 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-semibold border-2 transition min-h-[44px] ${
                isActive ? cls.active + " shadow-md" : cls.idle
              }`}
              data-testid={`tab-cat-${cat.key}`}
            >
              <Icon className="w-4 h-4" />
              {cat.label}
              <span className="opacity-60 text-xs">({count})</span>
              {sel > 0 && (
                <span className={`rounded-full px-1.5 text-[10px] font-bold ${isActive ? 'bg-white text-slate-800' : 'bg-white/80 text-slate-700'}`}>{sel}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Lista servizi raggruppata */}
      <div className="max-h-72 overflow-y-auto rounded-lg bg-slate-50 border border-slate-200 divide-y divide-slate-200">
        {filtered.length === 0 && (
          <p className="text-center text-slate-400 py-6 text-sm">Nessun servizio trovato</p>
        )}
        {filtered.map(cat => {
          const Icon = cat.icon;
          const cls = COLOR_MAP[cat.color] || COLOR_MAP.violet;
          return (
            <div key={cat.key} className="p-2" data-testid={`gruppo-cat-${cat.key}`}>
              {activeTab === "all" && (
                <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold mb-2 ${cls.idle}`}>
                  <Icon className="w-3 h-3" />
                  {cat.label}
                  <span className="opacity-60">({cat.items.length})</span>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {cat.items.map(servizio => {
                  const isSelected = selectedServizi.some(s => s.nome === servizio.nome);
                  return (
                    <button
                      key={servizio.id || servizio.nome}
                      type="button"
                      onClick={() => onToggle(servizio)}
                      className={`flex items-center gap-2 p-2.5 rounded-lg transition text-left min-h-[52px] ${
                        isSelected
                          ? "bg-blue-50 border-2 border-blue-400 shadow-sm"
                          : "bg-white border border-slate-200 hover:border-blue-300 hover:shadow-sm"
                      }`}
                      data-testid={`servizio-item-${servizio.id || servizio.nome}`}
                    >
                      <span
                        className="w-3 h-3 rounded-full shrink-0 ring-1 ring-white shadow-sm"
                        style={{ backgroundColor: servizio.colore || "#3B82F6" }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold truncate text-slate-800">{servizio.nome}</p>
                        <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mt-0.5">
                          <span>{servizio.durata_minuti}min</span>
                          <span>•</span>
                          <span className="font-medium">€{servizio.prezzo}</span>
                          {servizio.tempo_posa > 0 && (
                            <span className="text-amber-600 font-medium">+{servizio.tempo_posa}&apos; posa</span>
                          )}
                          {(servizio.steps?.length > 0) && (
                            <span className="text-violet-600 font-medium">{servizio.steps.length} step</span>
                          )}
                        </div>
                      </div>
                      {isSelected && (
                        <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center shrink-0">
                          <Check className="w-3.5 h-3.5 text-white" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
